import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { X, Check, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { usePerfil } from '../../context/PerfilContext';
import { useCerrarConEscape } from '../../hooks/useCerrarConEscape';
import {
  LIMITES_GRATIS, LIMITES_USO, NOMBRE_LIMITE, PRECIOS, VENTAJAS_PLUS, ClaveLimite, TipoCompra,
} from '../../config/planes';
import {
  OpcionCompra, comprasDisponibles, opcionesDeCompra, comprar, restaurar, GESTIONAR_SUSCRIPCION,
} from '../../utils/compras';
import { abrirEnlaceExterno, esApp } from '../../utils/plataforma';
import { NOMBRE_APP } from '../../config/marca';

/** Lo que es de Plus sin ser un «tienes N»: Préstamos y los topes de uso (LIMITES_USO). */
type OtroMotivo = 'prestamos' | 'informes' | 'informesPlus' | 'importar' | 'tickets' | 'tareasPlus' | 'applePay';

/** Por qué se abre: un límite concreto, otra función de Plus, o desde Configuración. */
export type MotivoOferta = ClaveLimite | OtroMotivo | undefined;

const OTROS: Record<OtroMotivo, { titulo: string; detalle: string }> = {
  prestamos: {
    titulo: 'Préstamos es de Plus',
    detalle: 'Puedes ver los que ya tienes y marcarlos como devueltos; para anotar nuevos, hazte Plus.',
  },
  informes: {
    titulo: `Con el plan gratis ves ${LIMITES_USO.mesesInformes} meses de informes`,
    detalle: 'Tus movimientos siguen todos ahí; con Plus, los informes llegan a todo tu histórico.',
  },
  informesPlus: {
    titulo: 'Este informe es de Plus',
    detalle: 'Patrimonio, fijos y variables, adelantos, días de la semana, comparar con el año pasado, el resumen de cada mes y el PDF completo.',
  },
  importar: {
    titulo: `Con el plan gratis importas ${LIMITES_USO.filasImportacion} filas cada vez`,
    detalle: `Divide el archivo en partes de ${LIMITES_USO.filasImportacion} filas, o hazte Plus e impórtalo de una vez.`,
  },
  tickets: {
    titulo: `Con el plan gratis caben ${LIMITES_USO.ticketsPorPlan} tickets por plan`,
    detalle: 'Los que ya hay siguen ahí; con Plus, fotos de tickets sin límite.',
  },
  applePay: {
    titulo: 'Apuntar al pagar con Apple Pay es de Plus',
    detalle: 'Al pagar con Apple Pay, myarinn se abre con el gasto, la tienda, la categoría de la última vez y la cuenta de cada tarjeta. Los atajos para apuntar a mano siguen siendo gratis.',
  },
  tareasPlus: {
    titulo: 'Esto de Tareas es de Plus',
    detalle: 'Tareas por turnos, el reparto contado en palabras, avisos al móvil de lo que te toca y tus tareas en el Calendario y en Recordatorios. Si quien organiza tu casa tiene Plus, también lo tienes tú.',
  },
};
const esOtro = (m: MotivoOferta): m is OtroMotivo => !!m && m in OTROS;

const EULA_APPLE = 'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/';

const ORDEN: TipoCompra[] = ['anual', 'mensual', 'vitalicio'];
const ETIQUETA: Record<TipoCompra, { nombre: string; detalle: string }> = {
  anual: { nombre: 'Anual', detalle: 'al año · la mejor opción' },
  mensual: { nombre: 'Mensual', detalle: 'al mes' },
  vitalicio: { nombre: 'De por vida', detalle: 'un solo pago' },
};

const fecha = (d: Date) => format(d, "d 'de' MMMM 'de' yyyy", { locale: es });

function titular(motivo: MotivoOferta): string {
  if (!motivo) return `${NOMBRE_APP} Plus`;
  if (esOtro(motivo)) return OTROS[motivo].titulo;
  const n = LIMITES_GRATIS[motivo];
  if (n === 0) return `${NOMBRE_LIMITE[motivo][1][0].toUpperCase()}${NOMBRE_LIMITE[motivo][1].slice(1)} es de Plus`;
  return `Con el plan gratis tienes ${n} ${NOMBRE_LIMITE[motivo][n === 1 ? 0 : 1]}`;
}

/**
 * La hoja de venta de myarinn Plus, y también su estado para quien ya lo
 * tiene. Se abre desde usePlus().ofrecerPlus(), nunca como un error.
 */
export const HojaPlus: React.FC<{ motivo: MotivoOferta; onClose: () => void }> = ({ motivo, onClose }) => {
  useCerrarConEscape(onClose);
  const { user } = useAuth();
  const { estadoPlus, perfil } = usePerfil();

  const [opciones, setOpciones] = useState<OpcionCompra[] | null>(null);
  const [elegida, setElegida] = useState<TipoCompra>('anual');
  const [ocupado, setOcupado] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user || !comprasDisponibles) return;
    opcionesDeCompra(user.uid)
      .then(setOpciones)
      .catch(e => { console.error('No se pudieron cargar los precios', e); setOpciones([]); });
  }, [user]);

  const opcion = opciones?.find(o => o.tipo === elegida);
  const precioDe = (t: TipoCompra) => opciones?.find(o => o.tipo === t)?.precio ?? PRECIOS[t];
  const pruebaDe = (t: TipoCompra) => opciones?.find(o => o.tipo === t)?.diasPrueba ?? 0;

  const pagar = async () => {
    if (!user || !opcion) return;
    setOcupado(true); setError(''); setMensaje('');
    try {
      if (await comprar(user.uid, opcion)) setMensaje('¡Listo! Ya tienes Plus. Gracias por apoyar la app.');
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'No se ha podido completar el pago.');
    } finally {
      setOcupado(false);
    }
  };

  const recuperar = async () => {
    if (!user) return;
    setOcupado(true); setError(''); setMensaje('');
    try {
      await restaurar(user.uid);
      setMensaje('Compras comprobadas. Si tenías Plus, ya está activo.');
    } catch (e: any) {
      setError(e?.message || 'No se han podido comprobar las compras.');
    } finally {
      setOcupado(false);
    }
  };

  // Quien ya lo tiene ve su estado, no la oferta
  const yaPlus = estadoPlus.esPlus && estadoPlus.motivo !== 'sin-limites' && estadoPlus.motivo !== 'regalo';
  const textoEstado = (() => {
    switch (estadoPlus.motivo) {
      case 'admin': return 'Como administrador tienes todo sin límites.';
      case 'vitalicio': return 'Tienes Plus de por vida. ¡Gracias!';
      case 'regalado': return 'Tienes Plus regalado.';
      case 'pagado': return `Tienes Plus. Se renueva el ${fecha(estadoPlus.hasta!)} salvo que lo canceles.`;
      case 'regalo': return `Por ser de los primeros, tienes Plus gratis hasta el ${fecha(estadoPlus.hasta!)}.`;
      case 'sin-limites': return 'De momento todo es gratis y sin límites.';
      default: return null;
    }
  })();
  const gestionar = perfil?.planOrigen ? GESTIONAR_SUSCRIPCION[perfil.planOrigen] : undefined;

  return (
    <div className="fixed inset-0 z-[55] bg-black/50 flex items-end sm:items-center justify-center capa-hoja"
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="titulo-plus"
        className="hoja bg-white dark:bg-gray-800 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl
                   max-h-full flex flex-col shadow-2xl">

        <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
              <Sparkles className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h2 id="titulo-plus" className="text-lg font-bold text-gray-900 dark:text-white">{titular(motivo)}</h2>
              {motivo && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {esOtro(motivo)
                    ? OTROS[motivo].detalle
                    : LIMITES_GRATIS[motivo] === 0
                      ? 'Lo que ya tengas sigue visible; para añadir más, hazte Plus.'
                      : 'Con Plus, sin límite. Lo que ya tienes sigue igual.'}
                </p>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar"
            className="boton-cerrar text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                       hover:bg-gray-100 dark:hover:bg-gray-700 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pb-4 space-y-4 overflow-y-auto">
          {textoEstado && (
            <p className="text-sm rounded-lg px-3 py-2 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200">
              {textoEstado}
            </p>
          )}

          <ul className="space-y-1.5">
            {VENTAJAS_PLUS.map(v => (
              <li key={v} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                <Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" /> {v}
              </li>
            ))}
          </ul>

          {!yaPlus && (
            <>
              <div className="grid gap-2" role="radiogroup" aria-label="Elige cómo pagar">
                {ORDEN.map(t => (
                  <button key={t} type="button" role="radio" aria-checked={elegida === t}
                    onClick={() => setElegida(t)}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl border text-left transition ${
                      elegida === t
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
                    }`}>
                    <span>
                      <span className="block font-semibold text-gray-900 dark:text-white">{ETIQUETA[t].nombre}</span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">{ETIQUETA[t].detalle}</span>
                    </span>
                    <span className="text-right">
                      <span className="block font-bold text-gray-900 dark:text-white">{precioDe(t)}</span>
                      {pruebaDe(t) > 0 && (
                        <span className="block text-xs text-emerald-700 dark:text-emerald-400">{pruebaDe(t)} días gratis</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>

              {mensaje && <p className="text-sm text-emerald-700 dark:text-emerald-400">{mensaje}</p>}
              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

              {comprasDisponibles ? (
                <button type="button" onClick={pagar} disabled={ocupado || !opcion}
                  className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold transition disabled:opacity-50">
                  {ocupado ? 'Un momento…' : opciones === null ? 'Cargando precios…'
                    : !opcion ? 'Esta opción no está disponible aquí'
                    : opcion.diasPrueba > 0 ? `Prueba ${opcion.diasPrueba} días gratis`
                    : `Hazte Plus por ${opcion.precio}`}
                </button>
              ) : (
                <p className="text-sm text-center text-gray-500 dark:text-gray-400">
                  Muy pronto podrás hacerte Plus desde aquí.
                </p>
              )}

              <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400">
                {opcion && opcion.diasPrueba > 0 && (
                  <>Los {opcion.diasPrueba} días de prueba son gratis; después se cobran {opcion.precio} si no
                    cancelas antes de que acaben. </>
                )}
                La suscripción mensual y la anual se renuevan solas al mismo precio hasta que las canceles,
                como tarde 24 horas antes de que acabe el periodo. {esApp
                  ? 'Se cobra en tu cuenta de Apple y se gestiona en Ajustes › tu nombre › Suscripciones.'
                  : 'Puedes cancelarla cuando quieras.'} La compra de por vida es un único pago.
                {' '}Plus vale en todos tus dispositivos con esta misma cuenta.
              </p>
            </>
          )}

          {yaPlus && gestionar && (
            <button type="button" onClick={() => abrirEnlaceExterno(gestionar)}
              className="w-full py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-sm font-semibold
                         text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
              Gestionar la suscripción
            </button>
          )}

          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
            {comprasDisponibles && esApp && (
              <button type="button" onClick={recuperar} disabled={ocupado} className="underline">
                Restaurar compras
              </button>
            )}
            <a href="/privacidad" className="underline">Privacidad</a>
            {esApp && (
              <button type="button" onClick={() => abrirEnlaceExterno(EULA_APPLE)} className="underline">
                Condiciones de uso
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
